import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  SNIPPET_BODY_MAX_BYTES,
  Snippet,
  SnippetField,
  SnippetOfflineError,
  createSnippet,
  deleteSnippet,
  recordSnippetUse,
  snippetBodyBytes,
  subscribeToSnippets,
  updateSnippet,
  validateSnippetInput,
} from '../../../services/snippetsService';
import {
  SNIPPET_VARIABLES,
  SnippetContext,
  findUnknownVariables,
  htmlToPlainText,
  plainTextToHtml,
  renderSnippetForInsert,
  unfilledTokenText,
} from '../utils/snippetVars';
import './SnippetPicker.scss';

/**
 * SnippetPicker - the reply-snippet library inside the composer.
 *
 * Mounted only while open (the composer renders it conditionally), so the
 * Firestore subscription lives exactly as long as the panel does.
 *
 * Layout: on desktop an in-flow panel under the composer header, the same shape as
 * the composer's invoice picker and send-later panels; on phones the very same
 * element becomes a fixed bottom sheet with a scrim (pure CSS, see the scss).
 *
 * Three views: `pick` (search + list, Enter/tap inserts), `manage` (edit / delete
 * with an inline confirm), `form` (create / edit, also "save this message").
 *
 * HARD RULES for anything in here, because it lives inside the composer's <form>:
 *   - every <button> is type="button" and there is no nested <form> (a submit
 *     would ARM A SEND of the email being written);
 *   - Enter in an <input> is always preventDefault-ed (implicit form submission);
 *   - keydown stops at the panel root so the composer's Ctrl+Enter "send" never
 *     fires from a key typed here.
 */

export type SnippetPickerCloseReason = 'escape' | 'dismiss' | 'inserted';

export interface SnippetPickerProps {
  /** What the variables resolve against (recipient, customer, sender). */
  context: SnippetContext;
  /** The owner-written part of the message (no quote, no signature): "save message as snippet". */
  currentBodyHtml?: string;
  /** Put the resolved HTML into the editor at the caret. Return false when that was not possible. */
  onInsert: (html: string, snippet: Snippet) => boolean;
  /** `escape` = return focus to where it was, `dismiss` = scrim tap, `inserted` = the editor already has focus. */
  onClose: (reason: SnippetPickerCloseReason) => void;
  /** Phone layout: do not autofocus the search box (the keyboard would cover the list). */
  isNarrow?: boolean;
}

type View = 'pick' | 'manage' | 'form';

interface FormState {
  id?: string;
  title: string;
  shortcut: string;
  text: string;
}

const EMPTY_FORM: FormState = { title: '', shortcut: '', text: '' };

let pickerSeq = 0;

/** Keep in step with `max-height: 78dvh` on the phone sheet in SnippetPicker.scss. */
const SHEET_MAX_FRACTION = 0.78;
/** Room left above the sheet (scrim to tap) when the keyboard leaves little visible height. */
const SHEET_TOP_GAP = 16;
/** Floor for a very short visible area (landscape phone + keyboard): head row + one control. */
const SHEET_MIN_HEIGHT = 120;

/** Exact shortcut first, then shortcut / title prefix, title, body; usage order within a rank. */
function filterSnippets(list: Snippet[], query: string): Snippet[] {
  const needle = query.trim().toLowerCase().replace(/^\//, '');
  if (!needle) return list;
  const scored: Array<{ s: Snippet; rank: number; i: number }> = [];
  list.forEach((s, i) => {
    const title = s.title.toLowerCase();
    const shortcut = (s.shortcut || '').toLowerCase();
    let rank = -1;
    if (shortcut && shortcut === needle) rank = 0;
    else if (shortcut && shortcut.indexOf(needle) === 0) rank = 1;
    else if (title.indexOf(needle) === 0) rank = 2;
    else if (title.indexOf(needle) >= 0) rank = 3;
    else if (s.bodyText.toLowerCase().indexOf(needle) >= 0) rank = 4;
    if (rank >= 0) scored.push({ s, rank, i });
  });
  scored.sort((a, b) => a.rank - b.rank || a.i - b.i);
  return scored.map((x) => x.s);
}

const firstLine = (text: string): string => {
  const line = text.split('\n').map((l) => l.trim()).find(Boolean) || '';
  return line.length > 110 ? `${line.slice(0, 107)}...` : line;
};

/** Bracketed terminal-style button label; the brackets are decoration, not part of the accessible name. */
const Br: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>
    <span className="snip__br" aria-hidden="true">[</span>
    {children}
    <span className="snip__br" aria-hidden="true">]</span>
  </>
);

const SnippetPicker: React.FC<SnippetPickerProps> = ({ context, currentBodyHtml, onInsert, onClose, isNarrow = false }) => {
  const uid = useRef(`snip${++pickerSeq}`).current;
  const listId = `${uid}-list`;

  const [snippets, setSnippets] = useState<Snippet[] | null>(null);
  // True while the list is the local cache's alone (see SnippetsMeta): offline, an EMPTY
  // snapshot says nothing about the real library, so it must not read as "no snippets yet".
  const [fromCache, setFromCache] = useState(false);
  const [online, setOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine !== false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [view, setView] = useState<View>('pick');
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  // `error` = a refusal the owner must notice (red, announced as an alert) rather than a confirmation.
  const [notice, setNotice] = useState<{ text: string; error?: boolean } | null>(null);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formBack, setFormBack] = useState<'pick' | 'manage'>('manage');
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<SnippetField, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [manageError, setManageError] = useState<string | null>(null);

  // iOS: fixed elements do not track the visual viewport, so with the keyboard
  // up the sheet's lower half (the form's buttons) would sit behind it.
  const [kbInset, setKbInset] = useState(0);
  // ...and the sheet's HEIGHT cap must follow the visible area too: the CSS cap is
  // a dvh, which does not shrink for the keyboard (only the visual viewport does),
  // so a bottom-anchored sheet taller than the visible area would push its top
  // (search box, title field, close button) off-screen with nothing to scroll it back.
  const [sheetMaxHeight, setSheetMaxHeight] = useState<number | null>(null);

  const searchRef = useRef<HTMLInputElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const shortcutRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const pendingCaretRef = useRef<number | null>(null);
  const insertedRef = useRef(false);

  // -- Library subscription ------------------------------------------------
  useEffect(() => {
    setSnippets(null);
    setFromCache(false);
    setLoadError(null);
    let unsub: (() => void) | undefined;
    try {
      unsub = subscribeToSnippets(
        (list, meta) => { setSnippets(list); setFromCache(Boolean(meta?.fromCache)); setLoadError(null); },
        (message) => setLoadError(message),
      );
    } catch {
      setLoadError('could not load snippets - check your connection');
    }
    return () => { try { unsub?.(); } catch { /* already gone */ } };
  }, [reloadKey]);

  // Only picks the wording of the "no answer from the server yet" status (offline vs loading).
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  useEffect(() => {
    if (!isNarrow) { setKbInset(0); setSheetMaxHeight(null); return undefined; }
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return undefined;
    const update = () => {
      setKbInset(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
      // Never taller than the stylesheet's 78% cap (the keyboard-down look), and
      // never taller than what is visible minus a sliver of scrim to tap on.
      setSheetMaxHeight(Math.round(Math.min(window.innerHeight * SHEET_MAX_FRACTION, Math.max(SHEET_MIN_HEIGHT, vv.height - SHEET_TOP_GAP))));
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [isNarrow]);

  // -- Derived -------------------------------------------------------------
  const filtered = useMemo(() => filterSnippets(snippets || [], query), [snippets, query]);
  const active = Math.min(activeIndex, Math.max(0, filtered.length - 1));
  const optionId = (s: Snippet) => `${uid}-opt-${s.id}`;
  const activeOptionId = view === 'pick' && filtered[active] ? optionId(filtered[active]) : undefined;

  const messageText = useMemo(() => htmlToPlainText(currentBodyHtml || ''), [currentBodyHtml]);

  useEffect(() => { setActiveIndex(0); }, [query]);

  // Keep the highlighted option visible while arrowing through a long list.
  useEffect(() => {
    if (!activeOptionId) return;
    const el = document.getElementById(activeOptionId);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [activeOptionId]);

  // Focus follows the view: the search box on desktop (never on phones - it
  // would raise the keyboard over the list), the title when a form opens.
  useEffect(() => {
    if (view === 'pick' && !isNarrow) searchRef.current?.focus();
    if (view === 'form') titleRef.current?.focus();
  }, [view, isNarrow]);

  // A variable chip inserted at the caret: put the caret back after the token
  // once React has re-rendered the textarea with the new text.
  useLayoutEffect(() => {
    const pos = pendingCaretRef.current;
    if (pos === null) return;
    pendingCaretRef.current = null;
    const el = textRef.current;
    if (el) { el.focus(); el.setSelectionRange(pos, pos); }
  }, [form.text]);

  // -- Actions -------------------------------------------------------------
  const insert = useCallback((s: Snippet) => {
    if (insertedRef.current) return;
    setNotice(null);
    // A body past the cap (written by another client, or flagged by the service when it
    // had to leave the body out) is refused rather than pasted into the editor and
    // mailed: the form never saves one, so this only guards what got into the library
    // some other way.
    if (s.oversize || snippetBodyBytes(s.bodyHtml) > SNIPPET_BODY_MAX_BYTES) {
      setNotice({
        text: `"${s.title}" is too long to insert (max ${SNIPPET_BODY_MAX_BYTES / 1024} KB) - shorten it under manage first`,
        error: true,
      });
      return;
    }
    const { html } = renderSnippetForInsert(s.bodyHtml, context);
    if (!html.replace(/<[^>]*>/g, '').trim()) { setNotice({ text: 'that snippet is empty' }); return; }
    if (!onInsert(html, s)) {
      setNotice({ text: "couldn't insert - the editor isn't ready, try again" });
      return;
    }
    insertedRef.current = true;
    try {
      // Fire-and-forget; the service swallows its own failures, this only guards a sync throw.
      void Promise.resolve(recordSnippetUse(s.id)).catch(() => undefined);
    } catch { /* usage counting is best-effort */ }
    onClose('inserted');
  }, [context, onInsert, onClose]);

  const openForm = (next: FormState, back: 'pick' | 'manage') => {
    setForm(next);
    setFormBack(back);
    setFieldErrors({});
    setFormError(null);
    setNotice(null);
    setView('form');
  };

  const goBack = () => {
    setNotice(null);
    if (view === 'form') { setView(formBack); return; }
    setConfirmDeleteId(null);
    setManageError(null);
    setView('pick');
  };

  // Keep the owner in the form AT the problem (a body error sits under a tall textarea).
  const focusFirstInvalid = (errors: Partial<Record<SnippetField, string>>) => {
    const el = errors.title ? titleRef.current : errors.shortcut ? shortcutRef.current : errors.body ? textRef.current : null;
    el?.focus();
  };

  // An old body error ("unknown placeholder ...") is about text that has since changed;
  // the live hint under the field takes over, and the next save re-checks for real.
  const clearBodyError = () => setFieldErrors((e) => (e.body ? { ...e, body: undefined } : e));

  const save = async () => {
    if (saving) return;
    const bodyHtml = plainTextToHtml(form.text);
    const input = { title: form.title, shortcut: form.shortcut, bodyHtml };
    const v = validateSnippetInput(input);
    if (!v.ok) { setFieldErrors(v.errors); setFormError(null); focusFirstInvalid(v.errors); return; }
    setSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      if (form.id) await updateSnippet(form.id, input);
      else await createSnippet(input);
      setNotice({ text: `saved "${v.value.title}"` });
      setView(formBack);
    } catch (err) {
      const errors = (err as { errors?: Partial<Record<SnippetField, string>> } | null)?.errors;
      if (errors && typeof errors === 'object') {
        setFieldErrors(errors);
        focusFirstInvalid(errors);
      } else {
        console.warn('[SnippetPicker] save failed:', (err as { code?: string })?.code || 'error');
        setFormError("couldn't save the snippet - check your connection and try again");
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s: Snippet) => {
    if (deleting) return;
    setDeleting(true);
    setManageError(null);
    try {
      await deleteSnippet(s.id);
      setConfirmDeleteId(null);
    } catch (err) {
      console.warn('[SnippetPicker] delete failed:', (err as { code?: string })?.code || 'error');
      const offline = err instanceof SnippetOfflineError || (err as { code?: string } | null)?.code === 'unavailable';
      setManageError(offline
        ? `couldn't delete "${s.title}" - check your connection and try again`
        : `couldn't delete "${s.title}" - try again`);
    } finally {
      setDeleting(false);
    }
  };

  const addVariable = (name: string) => {
    const token = `{{${name}}}`;
    const el = textRef.current;
    const start = el ? el.selectionStart : form.text.length;
    const end = el ? el.selectionEnd : form.text.length;
    pendingCaretRef.current = start + token.length;
    clearBodyError();
    setForm((f) => ({ ...f, text: f.text.slice(0, start) + token + f.text.slice(end) }));
  };

  const onRootKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' || ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'j')) {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === 'Escape' && view !== 'pick') goBack();
      else onClose('escape');
      return;
    }
    // Everything typed in the panel belongs to the panel (Ctrl+Enter = send, in the composer).
    e.stopPropagation();
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.nativeEvent as KeyboardEvent).isComposing) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(Math.min(filtered.length - 1, active + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(Math.max(0, active - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault(); // implicit submit of the composer form would arm a send
      const s = filtered[active];
      if (s) insert(s);
    }
  };

  const swallowEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.preventDefault();
  };

  const dupShortcut = useMemo(() => {
    const sc = form.shortcut.trim().toLowerCase();
    if (!sc) return null;
    return (snippets || []).find((s) => s.id !== form.id && s.shortcut === sc) || null;
  }, [form.shortcut, form.id, snippets]);

  // Typo'd placeholders ({{first name}}, {{customer.name}}) while typing: the save refuses
  // them too, but a hint under the field beats an error after the fact. Computed from the
  // HTML the save would store, so both read the very same text.
  const unknownVars = useMemo(
    () => (view === 'form' ? findUnknownVariables(plainTextToHtml(form.text)) : []),
    [view, form.text],
  );

  // -- Render --------------------------------------------------------------
  const showNoData = snippets === null;
  const emptyList = snippets !== null && snippets.length === 0;
  // Empty + cache-only = the server has not answered, NOT "the library is empty".
  const libraryPending = emptyList && fromCache;
  const libraryEmpty = emptyList && !fromCache;
  // The save's own (red) body error already names what is wrong; the amber hint is for the live state.
  const showUnknownHint = unknownVars.length > 0 && !fieldErrors.body;
  const listShown = !showNoData && !emptyList && filtered.length > 0;

  const explain = (
    <div className="snip__empty">
      <p>
        No snippets yet. A snippet is a reply you write once and drop into any email - quote wording,
        hosting renewals, &quot;thanks for your enquiry&quot;, payment details.
      </p>
      <p>
        Use variables like <code>{'{{firstName}}'}</code> to personalise it. The library is shared with
        everyone who uses this app.
      </p>
    </div>
  );

  const newButton = (back: 'pick' | 'manage') => (
    <button type="button" className="snip__btn snip__btn--primary" onClick={() => openForm(EMPTY_FORM, back)}>
      <Br>{back === 'pick' ? '+ create your first snippet' : '+ new'}</Br>
    </button>
  );

  const pendingStatus = (
    <div className={'snip__state' + (online ? '' : ' snip__state--warn')} role="status">
      {online ? 'loading snippets...' : 'offline - snippets will load when you are back online'}
    </div>
  );

  const noticeBlock = notice && (
    <div className={'snip__state ' + (notice.error ? 'snip__state--error' : 'snip__state--note')} role={notice.error ? 'alert' : 'status'}>
      {notice.text}
    </div>
  );

  const statusBlock = showNoData && (
    loadError ? (
      <div className="snip__state snip__state--error" role="alert">
        <span>{loadError}</span>
        <button type="button" className="snip__btn" onClick={() => setReloadKey((k) => k + 1)}><Br>retry</Br></button>
      </div>
    ) : (
      <div className="snip__state" role="status">loading snippets...</div>
    )
  );

  return (
    <div className="snip" role="dialog" aria-label="Reply snippets" onKeyDown={onRootKeyDown}>
      <div className="snip__scrim" onClick={() => onClose('dismiss')} aria-hidden="true" />
      <div
        className="snip__panel"
        style={isNarrow && kbInset > 0
          ? { bottom: kbInset, ...(sheetMaxHeight !== null ? { maxHeight: sheetMaxHeight } : null) }
          : undefined}
      >
        {/* -- pick ------------------------------------------------------ */}
        {view === 'pick' && (
          <>
            <div className="snip__head">
              <input
                ref={searchRef}
                className="snip__search"
                type="text"
                role="combobox"
                aria-label="Search snippets"
                aria-autocomplete="list"
                aria-expanded={listShown}
                aria-controls={listShown ? listId : undefined}
                aria-activedescendant={activeOptionId}
                placeholder="search snippets..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKeyDown}
                enterKeyHint="go"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
              />
              <span className="snip__tools">
                <button type="button" className="snip__btn" onClick={() => { setNotice(null); setView('manage'); }}>
                  <Br>manage</Br>
                </button>
                <button
                  type="button"
                  className="snip__btn"
                  onClick={() => openForm({ ...EMPTY_FORM, text: messageText }, 'pick')}
                  disabled={!messageText.trim()}
                  title={messageText.trim() ? 'Save what you have written as a new snippet' : 'Write a message first'}
                >
                  <Br>save message</Br>
                </button>
                <button type="button" className="snip__btn snip__btn--close" aria-label="Close snippets" onClick={() => onClose('dismiss')}>
                  <Br>×</Br>
                </button>
              </span>
            </div>

            {noticeBlock}
            {showNoData ? statusBlock : (
              <>
                {loadError && <div className="snip__state snip__state--error" role="alert">{loadError}</div>}
                {libraryEmpty ? (
                  <>
                    {explain}
                    <div className="snip__row-actions">{newButton('pick')}</div>
                  </>
                ) : libraryPending ? pendingStatus : filtered.length === 0 ? (
                  <div className="snip__state" role="status">no snippets match &quot;{query.trim()}&quot;</div>
                ) : (
                  <ul className="snip__list" role="listbox" id={listId} aria-label="Snippets">
                    {filtered.map((s, i) => (
                      <li
                        key={s.id}
                        id={optionId(s)}
                        role="option"
                        aria-selected={i === active}
                        className={'snip__opt' + (i === active ? ' snip__opt--active' : '')}
                        onClick={() => insert(s)}
                        onMouseMove={() => { if (i !== active) setActiveIndex(i); }}
                      >
                        <span className="snip__opt-top">
                          <span className="snip__title">{s.title}</span>
                          {s.shortcut && <span className="snip__short">/{s.shortcut}</span>}
                        </span>
                        <span className="snip__preview">{firstLine(s.bodyText)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
            {!isNarrow && <div className="snip__hint">up/down choose - enter insert - esc close</div>}
          </>
        )}

        {/* -- manage ---------------------------------------------------- */}
        {view === 'manage' && (
          <>
            <div className="snip__head">
              <span className="snip__heading">manage snippets</span>
              <span className="snip__tools">
                {newButton('manage')}
                <button type="button" className="snip__btn" onClick={goBack}><Br>done</Br></button>
              </span>
            </div>
            {noticeBlock}
            {manageError && <div className="snip__state snip__state--error" role="alert">{manageError}</div>}
            {showNoData ? statusBlock : libraryEmpty ? explain : libraryPending ? pendingStatus : (
              <ul className="snip__list snip__list--manage">
                {(snippets || []).map((s) => (
                  <li key={s.id} className="snip__mrow">
                    <span className="snip__mrow-text">
                      <span className="snip__opt-top">
                        <span className="snip__title">{s.title}</span>
                        {s.shortcut && <span className="snip__short">/{s.shortcut}</span>}
                      </span>
                      <span className="snip__preview">{firstLine(s.bodyText)}</span>
                    </span>
                    {confirmDeleteId === s.id ? (
                      <span className="snip__confirm" role="group" aria-label={`Delete ${s.title}?`}>
                        <span className="snip__confirm-q">delete &quot;{s.title}&quot;?</span>
                        <button type="button" className="snip__btn snip__btn--danger" disabled={deleting} onClick={() => void remove(s)}>
                          <Br>{deleting ? 'deleting...' : 'yes, delete'}</Br>
                        </button>
                        <button type="button" className="snip__btn" disabled={deleting} onClick={() => setConfirmDeleteId(null)}>
                          <Br>keep</Br>
                        </button>
                      </span>
                    ) : (
                      <span className="snip__mrow-actions">
                        <button
                          type="button"
                          className="snip__btn"
                          aria-label={`Edit ${s.title}`}
                          onClick={() => openForm({ id: s.id, title: s.title, shortcut: s.shortcut || '', text: s.bodyText }, 'manage')}
                        >
                          <Br>edit</Br>
                        </button>
                        <button
                          type="button"
                          className="snip__btn snip__btn--danger"
                          aria-label={`Delete ${s.title}`}
                          onClick={() => { setManageError(null); setConfirmDeleteId(s.id); }}
                        >
                          <Br>delete</Br>
                        </button>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        {/* -- form ------------------------------------------------------ */}
        {view === 'form' && (
          <div className="snip__form" role="group" aria-label={form.id ? 'Edit snippet' : 'New snippet'}>
            <div className="snip__head">
              <span className="snip__heading">{form.id ? 'edit snippet' : 'new snippet'}</span>
            </div>

            <label className="snip__label" htmlFor={`${uid}-title`}>title</label>
            <input
              id={`${uid}-title`}
              ref={titleRef}
              className="snip__input"
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              onKeyDown={swallowEnter}
              placeholder="e.g. Hosting renewal"
              aria-invalid={fieldErrors.title ? true : undefined}
              aria-describedby={fieldErrors.title ? `${uid}-title-err` : undefined}
              autoComplete="off"
            />
            {fieldErrors.title && <div className="snip__err" id={`${uid}-title-err`} role="alert">{fieldErrors.title}</div>}

            <label className="snip__label" htmlFor={`${uid}-short`}>shortcut (optional)</label>
            <input
              id={`${uid}-short`}
              ref={shortcutRef}
              className="snip__input"
              type="text"
              value={form.shortcut}
              // Phones capitalise the first letter and love spaces: fold to the allowed alphabet as they type.
              onChange={(e) => setForm((f) => ({
                ...f,
                shortcut: e.target.value.toLowerCase().replace(/[\s_]+/g, '-').replace(/[^a-z0-9-]/g, ''),
              }))}
              onKeyDown={swallowEnter}
              placeholder="e.g. renew - type it in the search box to find this fast"
              aria-invalid={fieldErrors.shortcut ? true : undefined}
              aria-describedby={fieldErrors.shortcut ? `${uid}-short-err` : undefined}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
            />
            {fieldErrors.shortcut && <div className="snip__err" id={`${uid}-short-err`} role="alert">{fieldErrors.shortcut}</div>}
            {!fieldErrors.shortcut && dupShortcut && (
              <div className="snip__warn" role="status">also used by &quot;{dupShortcut.title}&quot;</div>
            )}

            <label className="snip__label" htmlFor={`${uid}-text`}>text</label>
            <textarea
              id={`${uid}-text`}
              ref={textRef}
              className="snip__textarea"
              rows={8}
              value={form.text}
              onChange={(e) => { clearBodyError(); setForm((f) => ({ ...f, text: e.target.value })); }}
              placeholder={'Hi {{firstName}},\n\nThanks for getting in touch...'}
              aria-invalid={fieldErrors.body ? true : undefined}
              aria-describedby={`${uid}-vars${fieldErrors.body ? ` ${uid}-text-err` : ''}${showUnknownHint ? ` ${uid}-unknown` : ''}`}
            />
            {fieldErrors.body && <div className="snip__err snip__err--block" id={`${uid}-text-err`} role="alert">{fieldErrors.body}</div>}
            {showUnknownHint && (
              <div className="snip__warn" id={`${uid}-unknown`} role="status">
                {unknownVars.length > 1 ? 'these are' : 'this is'} not a variable that fills in: {unknownVars.map(unfilledTokenText).join(', ')}.
                {' '}It would reach the client as typed, so the snippet will not save until {unknownVars.length > 1 ? 'they are' : 'it is'} fixed
                {' '}- use one of the variables below.
              </div>
            )}

            <div className="snip__vars" role="group" aria-label="Insert a variable">
              {SNIPPET_VARIABLES.map((v) => (
                <button
                  key={v.name}
                  type="button"
                  className="snip__chip"
                  title={v.hint}
                  onMouseDown={(e) => e.preventDefault() /* keep the caret in the textarea */}
                  onClick={() => addVariable(v.name)}
                >
                  {`{{${v.name}}}`}
                </button>
              ))}
            </div>
            <div className="snip__hint snip__hint--wrap" id={`${uid}-vars`}>
              Variables fill in when you insert the snippet:{' '}
              {SNIPPET_VARIABLES.map((v) => `{{${v.name}}} = ${v.hint}`).join(' - ')}.
              One that cannot be filled is left for you to replace, and the email will not send until it is.
            </div>

            {formError && <div className="snip__err" role="alert">{formError}</div>}
            <div className="snip__row-actions">
              <button type="button" className="snip__btn snip__btn--primary" onClick={() => void save()} disabled={saving}>
                <Br>{saving ? 'saving...' : 'save'}</Br>
              </button>
              <button type="button" className="snip__btn" onClick={goBack} disabled={saving}><Br>cancel</Br></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SnippetPicker;
